import React, { useMemo, useState } from "react";
import { Box, Chip, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import GenericTable, { ColumnDefinition } from "../common/generic-table";
import SearchBar from "../common/search-bar";
import { BugLogDto, BugPriority } from "../../services/bugLogService";
import { useHasPermission } from "../../hooks/useHasPermission";

interface BugReportListTableProps {
  reports: BugLogDto[];
  loading?: boolean;
  isGlobalAdminContext: boolean;
  tenantNameById?: Record<string, string>;
  onEdit: (report: BugLogDto) => void;
  onDelete: (report: BugLogDto) => void;
}

const priorityColorMap: Record<BugPriority, "success" | "warning" | "error" | "secondary"> = {
  Low: "success",
  Medium: "warning",
  High: "error",
  Critical: "secondary",
};

const statusColorMap: Record<
  BugLogDto["status"],
  "success" | "warning" | "info" | "default"
> = {
  "In Progress": "warning",
  "Dev Done": "info",
  Done: "success",
  UAT: "warning",
};

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

const resolveTenantDisplayName = (
  report: BugLogDto,
  tenantNameById: Record<string, string>
) => {
  const tenantId = (report.tenantId ?? "").trim();
  if (!tenantId || tenantId === EMPTY_GUID) {
    return "Global Admin";
  }

  return tenantNameById[tenantId] ?? tenantId;
};

const formatDate = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  const formatted = date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });

  return formatted;
};

const BugReportListTable: React.FC<BugReportListTableProps> = ({
  reports,
  loading = false,
  isGlobalAdminContext,
  tenantNameById = {},
  onEdit,
  onDelete,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const canUpdateBug = useHasPermission("accessmanagement", "update");
  const canDeleteBug = useHasPermission("accessmanagement", "delete");

  const filteredReports = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return reports;
    }

    return reports.filter((report) => {
      const tenantName = resolveTenantDisplayName(report, tenantNameById);
      const title = (report.title ?? "").toLowerCase();
      const category = (report.category ?? "").toLowerCase();
      const priority = (report.priority ?? "").toLowerCase();
      const status = (report.status ?? "").toLowerCase();
      const createdBy = (report.createdBy ?? "").toLowerCase();
      const tenantId = (report.tenantId ?? "").toLowerCase();
      const tenantNameNormalized = (tenantName ?? "").toLowerCase();

      return (
        title.includes(query) ||
        category.includes(query) ||
        priority.includes(query) ||
        status.includes(query) ||
        createdBy.includes(query) ||
        tenantId.includes(query) ||
        tenantNameNormalized.includes(query)
      );
    });
  }, [reports, searchQuery, tenantNameById]);

  const columns: ColumnDefinition<BugLogDto>[] = useMemo(() => {
    const baseColumns: ColumnDefinition<BugLogDto>[] = [
      {
        id: "title",
        label: "Title",
        minWidth: 180,
        format: (report) => report.title,
      },
      {
        id: "category",
        label: "Category",
        minWidth: 120,
        format: (report) => report.category,
      },
      {
        id: "priority",
        label: "Priority",
        minWidth: 120,
        format: (report) => (
          <Chip
            label={report.priority}
            color={priorityColorMap[report.priority]}
            size="small"
            variant="outlined"
          />
        ),
      },
      {
        id: "status",
        label: "Status",
        minWidth: 120,
        format: (report) => (
          <Chip
            label={report.status}
            color={statusColorMap[report.status]}
            size="small"
            variant="outlined"
          />
        ),
      },
      {
        id: "createdBy",
        label: "Logged By",
        minWidth: 180,
        format: (report) => report.createdBy,
      },
    ];

    if (isGlobalAdminContext) {
      baseColumns.push({
        id: "tenantId",
        label: "Tenant",
        minWidth: 160,
        format: (report) => resolveTenantDisplayName(report, tenantNameById),
      });
    }

    baseColumns.push({
      id: "createdAt",
      label: "Created",
      minWidth: 180,
      format: (report) => formatDate(report.createdAt),
    });

    return baseColumns;
  }, [isGlobalAdminContext, tenantNameById]);

  return (
    <Box>
      <SearchBar
        placeholder="Search bug reports by title, category, status or user..."
        value={searchQuery}
        onChange={setSearchQuery}
      />
      <GenericTable
        columns={columns}
        data={filteredReports}
        getRowKey={(report) => report.id}
        loading={loading}
        emptyMessage={
          searchQuery
            ? "No bug reports found matching your search"
            : "No bug reports found"
        }
        actionsColumn={{
          label: "Actions",
          align: "right",
          render: (report) => (
            <Box className="user-table-actions">
              {canUpdateBug && (
                <IconButton
                  aria-label="edit bug report"
                  onClick={() => onEdit(report)}
                  className="user-table-edit-button"
                >
                  <EditIcon />
                </IconButton>
              )}
              {canDeleteBug && (
                <IconButton
                  aria-label="delete bug report"
                  onClick={() => onDelete(report)}
                  className="user-table-delete-button"
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          ),
        }}
        showResultsCount={true}
      />
    </Box>
  );
};

export default BugReportListTable;